import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './PopupTooltip.css';

export interface PopupTooltipProps {
  /** Content to display in the tooltip */
  content: React.ReactNode;
  /** Child element that triggers the tooltip */
  children: React.ReactElement;
  /** Tooltip placement relative to trigger element */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** Offset from trigger element in pixels */
  offset?: number;
  /** Custom arrow offset from top (for left/right placements) or left (for top/bottom placements) in pixels */
  arrowOffset?: number;
  /** Delay before showing tooltip (ms) */
  showDelay?: number;
  /** Delay before hiding tooltip (ms) */
  hideDelay?: number;
  /** Maximum width of tooltip */
  maxWidth?: string;
  /** Custom className for tooltip content */
  className?: string;
  /** Whether tooltip is disabled */
  disabled?: boolean;
  /** Trigger mode: 'hover', 'click', 'focus', or 'manual' */
  trigger?: 'hover' | 'click' | 'focus' | 'manual';
  /** Whether tooltip is visible (for manual mode) */
  visible?: boolean;
  /** Callback when tooltip visibility changes */
  onVisibilityChange?: (visible: boolean) => void;
}

/**
 * PopupTooltip - A professional, reusable tooltip component that renders above all windows
 * using React Portal to escape container overflow issues.
 * 
 * Features:
 * - Renders via Portal to document.body (always above all windows)
 * - Professional bold styling with distinctive shadows
 * - Smart positioning with scroll/resize handling
 * - Multiple trigger modes (hover, click, focus, manual)
 * - Fully accessible with keyboard support
 * 
 * @example
 * ```tsx
 * <PopupTooltip content="This is a helpful tooltip">
 *   <button>Hover me</button>
 * </PopupTooltip>
 * ```
 */
export const PopupTooltip: React.FC<PopupTooltipProps> = ({
  content,
  children,
  placement = 'top',
  offset = 8,
  arrowOffset: customArrowOffset,
  showDelay = 200,
  hideDelay = 100,
  maxWidth = '320px',
  className = '',
  disabled = false,
  trigger = 'hover',
  visible: controlledVisible,
  onVisibilityChange,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; arrowPosition?: 'left' | 'right' | 'top' | 'bottom'; arrowOffset?: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMouseOverTriggerRef = useRef(false);
  const isMouseOverTooltipRef = useRef(false);

  // Use controlled visibility if provided, otherwise use internal state
  const visible = controlledVisible !== undefined ? controlledVisible : isVisible;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipRef.current?.offsetWidth || 320;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 100;
    const scrollX = window.scrollX || window.pageXOffset;
    const scrollY = window.scrollY || window.pageYOffset;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let arrowPosition: 'left' | 'right' | 'top' | 'bottom' = placement;
    let arrowOffset = 0; // Offset for arrow positioning (for left/right placements)

    switch (placement) {
      case 'top':
        top = rect.top + scrollY - tooltipHeight - offset;
        left = rect.left + rect.width / 2 + scrollX;
        // Adjust if tooltip goes off screen
        if (left - tooltipWidth / 2 < scrollX + 10) {
          left = scrollX + tooltipWidth / 2 + 10;
          arrowPosition = 'left';
        } else if (left + tooltipWidth / 2 > scrollX + viewportWidth - 10) {
          left = scrollX + viewportWidth - tooltipWidth / 2 - 10;
          arrowPosition = 'right';
        }
        break;

      case 'bottom':
        top = rect.bottom + scrollY + offset;
        left = rect.left + rect.width / 2 + scrollX;
        // Adjust if tooltip goes off screen
        if (left - tooltipWidth / 2 < scrollX + 10) {
          left = scrollX + tooltipWidth / 2 + 10;
          arrowPosition = 'left';
        } else if (left + tooltipWidth / 2 > scrollX + viewportWidth - 10) {
          left = scrollX + viewportWidth - tooltipWidth / 2 - 10;
          arrowPosition = 'right';
        }
        break;

      case 'left':
        // Align to top of trigger, but adjust if it goes off screen
        top = rect.top + scrollY;
        left = rect.left + scrollX - tooltipWidth - offset;
        arrowOffset = 0;
        // Adjust if tooltip goes off top of screen
        if (top < scrollY + 10) {
          top = scrollY + 10;
          arrowPosition = 'top';
        } 
        // Adjust if tooltip goes off bottom of screen - move it up
        else if (top + tooltipHeight > scrollY + viewportHeight - 10) {
          top = Math.max(scrollY + 10, scrollY + viewportHeight - tooltipHeight - 10);
          // Calculate how much we moved up
          const movedUp = (rect.top + scrollY) - top;
          // If we moved up significantly, position arrow near top
          if (movedUp > 20) {
            arrowPosition = 'right';
            arrowOffset = 20; // Position arrow 20px from top
          } else {
            arrowPosition = 'right';
            arrowOffset = 0; // Arrow at top
          }
        }
        // If tooltip fits, position arrow at top of trigger
        else {
          arrowPosition = 'right';
          arrowOffset = 0;
        }
        break;

      case 'right': {
        // Align to top of trigger, but adjust if it goes off screen
        top = rect.top + scrollY;
        left = rect.right + scrollX + offset;
        
        // Always set arrow position to 'left' (arrow on left side pointing right)
        arrowPosition = 'left';
        
        // Use custom arrow offset if provided, otherwise calculate default
        if (customArrowOffset !== undefined) {
          arrowOffset = customArrowOffset;
        } else {
          arrowOffset = 0;
        }
        
        // Calculate available space
        const bottomMargin = 20; // Minimum margin from bottom of viewport
        const maxTop = scrollY + viewportHeight - tooltipHeight - bottomMargin;
        
        // Adjust if tooltip goes off top of screen
        if (top < scrollY + 10) {
          top = scrollY + 10;
          // If custom offset provided, keep it; otherwise reset to 0
          if (customArrowOffset === undefined) {
            arrowOffset = 0;
          }
        } 
        // Adjust if tooltip goes off bottom of screen - move it up to fit
        else if (top + tooltipHeight > scrollY + viewportHeight - bottomMargin) {
          // Position tooltip so it fits within viewport with margin
          top = Math.max(scrollY + 10, maxTop);
          // Calculate how much we moved up from trigger top
          const movedUp = (rect.top + scrollY) - top;
          // If custom offset provided, use it; otherwise calculate based on movement
          if (customArrowOffset === undefined) {
            if (movedUp > 30) {
              arrowOffset = Math.min(30, rect.height / 2); // Position arrow near trigger center if possible
            } else {
              arrowOffset = 0; // Arrow at top of tooltip (aligned with trigger top)
            }
          }
          // customArrowOffset already set above, keep it
        }
        // If tooltip fits, custom offset already set above, keep it
        break;
      }
    }

    setPosition({ top, left, arrowPosition, arrowOffset });
  }, [placement, offset, customArrowOffset]);

  const showTooltip = useCallback(() => {
    if (disabled) return;

    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }

    showTimeoutRef.current = setTimeout(() => {
      if (controlledVisible === undefined) {
        setIsVisible(true);
      }
      onVisibilityChange?.(true);
      // Update position after a brief delay to ensure tooltip is rendered
      setTimeout(updatePosition, 10);
    }, showDelay);
  }, [disabled, showDelay, controlledVisible, onVisibilityChange, updatePosition]);

  const hideTooltip = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    hideTimeoutRef.current = setTimeout(() => {
      // Only hide if mouse is not over trigger or tooltip
      if (!isMouseOverTriggerRef.current && !isMouseOverTooltipRef.current) {
        if (controlledVisible === undefined) {
          setIsVisible(false);
        }
        onVisibilityChange?.(false);
      }
    }, hideDelay);
  }, [hideDelay, controlledVisible, onVisibilityChange]);

  // Check if mouse is over trigger or tooltip
  const checkMousePosition = useCallback((e: MouseEvent) => {
    const target = e.target as Node;
    const isOverTrigger = triggerRef.current?.contains(target) || false;
    const isOverTooltip = tooltipRef.current?.contains(target) || false;
    
    isMouseOverTriggerRef.current = isOverTrigger;
    isMouseOverTooltipRef.current = isOverTooltip;
    
    // If mouse is not over either, hide tooltip
    if (!isOverTrigger && !isOverTooltip && visible) {
      hideTooltip();
    }
  }, [visible, hideTooltip]);

  // Update position when visible
  useEffect(() => {
    if (visible) {
      updatePosition();

      // Update position on scroll/resize
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [visible, updatePosition]);

  // Handle trigger events
  useEffect(() => {
    const element = triggerRef.current;
    if (!element || trigger === 'manual') return;

    if (trigger === 'hover') {
      const handleMouseEnter = () => {
        isMouseOverTriggerRef.current = true;
        showTooltip();
      };
      const handleMouseLeave = () => {
        isMouseOverTriggerRef.current = false;
        // Delay hide to allow mouse to move to tooltip
        hideTooltip();
      };
      
      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);
      element.addEventListener('focus', showTooltip);
      element.addEventListener('blur', hideTooltip);
      
      return () => {
        element.removeEventListener('mouseenter', handleMouseEnter);
        element.removeEventListener('mouseleave', handleMouseLeave);
        element.removeEventListener('focus', showTooltip);
        element.removeEventListener('blur', hideTooltip);
      };
    } else if (trigger === 'click') {
      const handleClick = (e: Event) => {
        e.stopPropagation();
        if (visible) {
          hideTooltip();
        } else {
          showTooltip();
        }
      };
      element.addEventListener('click', handleClick);

      // Close on outside click
      const handleOutsideClick = (e: MouseEvent) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(e.target as Node) &&
          !element.contains(e.target as Node)
        ) {
          hideTooltip();
        }
      };
      document.addEventListener('click', handleOutsideClick);

      return () => {
        element.removeEventListener('click', handleClick);
        document.removeEventListener('click', handleOutsideClick);
      };
    } else if (trigger === 'focus') {
      element.addEventListener('focus', showTooltip);
      element.addEventListener('blur', hideTooltip);
      
      return () => {
        element.removeEventListener('focus', showTooltip);
        element.removeEventListener('blur', hideTooltip);
      };
    }
  }, [trigger, showTooltip, hideTooltip, visible]);
  
  // Handle tooltip mouse events (for hover mode)
  useEffect(() => {
    if (trigger !== 'hover' || !visible) return;
    
    const tooltip = tooltipRef.current;
    if (!tooltip) return;
    
    const handleTooltipMouseEnter = () => {
      isMouseOverTooltipRef.current = true;
      // Cancel any pending hide
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
    
    const handleTooltipMouseLeave = () => {
      isMouseOverTooltipRef.current = false;
      // Hide tooltip when mouse leaves tooltip
      hideTooltip();
    };
    
    tooltip.addEventListener('mouseenter', handleTooltipMouseEnter);
    tooltip.addEventListener('mouseleave', handleTooltipMouseLeave);
    
    return () => {
      tooltip.removeEventListener('mouseenter', handleTooltipMouseEnter);
      tooltip.removeEventListener('mouseleave', handleTooltipMouseLeave);
    };
  }, [trigger, visible, hideTooltip]);
  
  // Handle click outside to hide tooltip (for hover mode)
  useEffect(() => {
    if (trigger !== 'hover' || !visible) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const isClickOnTrigger = triggerRef.current?.contains(target) || false;
      const isClickOnTooltip = tooltipRef.current?.contains(target) || false;
      
      if (!isClickOnTrigger && !isClickOnTooltip) {
        if (controlledVisible === undefined) {
          setIsVisible(false);
        }
        onVisibilityChange?.(false);
      }
    };
    
    // Also handle mouse move to check position
    const handleMouseMove = (e: MouseEvent) => {
      checkMousePosition(e);
    };
    
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [trigger, visible, controlledVisible, onVisibilityChange, checkMousePosition]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Clone child element with ref
  // TypeScript doesn't expose ref on ReactElement, so we need to check it carefully
  const originalRef = (children as any).ref;
  const triggerElement = React.cloneElement(children as React.ReactElement<any>, {
    ref: (node: HTMLElement | null) => {
      if (node) {
        triggerRef.current = node;
        // Preserve original ref if it exists
        if (typeof originalRef === 'function') {
          originalRef(node);
        } else if (originalRef && typeof originalRef === 'object' && 'current' in originalRef) {
          // Use Object.assign to avoid read-only property error
          Object.assign(originalRef, { current: node });
        }
      }
    },
  } as any);

  // Always render tooltip in portal for smooth animations (even when hidden)
  // This ensures the exit animation plays properly
  const shouldRenderTooltip = content && (visible || (tooltipRef.current && !visible));

  const tooltipContent = shouldRenderTooltip ? (
    <div
      ref={tooltipRef}
      className={`popup-tooltip ${visible ? 'popup-tooltip-visible' : 'popup-tooltip-hidden'} ${className}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        maxWidth,
      }}
      role="tooltip"
      aria-live="polite"
      aria-hidden={!visible}
    >
      <div 
        className={`popup-tooltip-arrow popup-tooltip-arrow-${position.arrowPosition || placement}${(position.arrowPosition === 'left' || position.arrowPosition === 'right') && position.arrowOffset !== undefined ? ' popup-tooltip-arrow-custom-offset' : ''}`}
        style={
          (position.arrowPosition === 'left' || position.arrowPosition === 'right') && position.arrowOffset !== undefined
            ? { 
                '--arrow-offset': `${position.arrowOffset}px`,
                top: `${position.arrowOffset}px`, 
                transform: 'none',
                right: position.arrowPosition === 'left' ? '100%' : undefined,
                left: position.arrowPosition === 'right' ? '100%' : undefined
              } as React.CSSProperties & { '--arrow-offset'?: string }
            : undefined
        }
      />
      <div className="popup-tooltip-content">
        {content}
      </div>
    </div>
  ) : null;

  // Only render portal after component is mounted (client-side only)
  // This prevents React error #310 (hydration mismatch)
  if (!isMounted || typeof document === 'undefined' || !document.body) {
    return <>{triggerElement}</>;
  }

  return (
    <>
      {triggerElement}
      {tooltipContent && createPortal(tooltipContent, document.body)}
    </>
  );
};

/**
 * Hook for managing popup tooltip state programmatically
 * 
 * @example
 * ```tsx
 * const { showTooltip, hideTooltip, isVisible } = usePopupTooltip();
 * 
 * return (
 *   <>
 *     <button onClick={showTooltip}>Show Tooltip</button>
 *     <PopupTooltip 
 *       trigger="manual" 
 *       visible={isVisible}
 *       content="This is a tooltip"
 *     >
 *       <span>Target</span>
 *     </PopupTooltip>
 *   </>
 * );
 * ```
 */
export const usePopupTooltip = () => {
  const [isVisible, setIsVisible] = useState(false);

  const showTooltip = useCallback(() => setIsVisible(true), []);
  const hideTooltip = useCallback(() => setIsVisible(false), []);
  const toggleTooltip = useCallback(() => setIsVisible(prev => !prev), []);

  return {
    isVisible,
    showTooltip,
    hideTooltip,
    toggleTooltip,
  };
};

